import React, { useEffect, useRef } from 'react';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import { DeviceSwitchPortsDataType, VlanDataType } from '../../../types/buildingTypes';
import Typography from '@mui/material/Typography';
import InfoRow from '../common/InfoRow';
import dialogStyles from '../common/dialogStyles';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import config from '../../../config';

interface PortFlipFormProps {
  selectedPorts: DeviceSwitchPortsDataType[];
  availableVlans: VlanDataType | null;
  selectedVlan: string;
  setSelectedVlan: (val: string) => void;
  description: string;
  setDescription: (val: string) => void;
  isVlanValid: boolean;
  allowedInputCharacters: RegExp;
  isDescriptionValid: boolean;
  touched: { selectedVlan: boolean; newDescription: boolean };
  markVlanSelectTouched: () => void;
  markDescriptionTouched: () => void;
  submitAttempted: boolean;
  isJuniper: boolean;
  isDescriptionEditable: boolean;
  isDistro?: boolean;
}

/**
 * A predefined list of active statuses of ports.
 */
export const ACTIVE_PORT_STATUSES = ['up', 'connected', 'forward'];

const PortFlipForm: React.FC<PortFlipFormProps> = ({
  selectedPorts,
  availableVlans,
  selectedVlan,
  setSelectedVlan,
  description,
  setDescription,
  isVlanValid,
  allowedInputCharacters,
  isDescriptionValid,
  touched,
  markVlanSelectTouched,
  markDescriptionTouched,
  submitAttempted,
  isJuniper,
  isDescriptionEditable,
  isDistro = false,
}) => {
  const [hasActivePorts, setHasActivePorts] = React.useState(false);
  const [hasMultipleActivePorts, setHasMultipleActivePorts] = React.useState(false);

  useEffect(() => {
    const activePortCount = selectedPorts.reduce(
      (count, port_item) =>
        ACTIVE_PORT_STATUSES.includes(port_item.status.toLowerCase()) ? count + 1 : count,
      0
    );
    setHasActivePorts(activePortCount > 0);
    setHasMultipleActivePorts(activePortCount > 1);

    if (!isDescriptionEditable) return;

    if (availableVlans?.type === 'juniper') {
      const item = availableVlans.available_vlans.find((i) => i.port_usage === selectedVlan);
      if (item) {
        setDescription(item?.port_usage || '');
      }
    } else {
      const item = availableVlans?.available_vlans.find((i) => i.vlan === selectedVlan);
      if (item) {
        setDescription(item?.description || '');
      }
    }
  }, [selectedVlan, availableVlans, isDescriptionEditable]);

  useEffect(() => {
    if (!isDescriptionEditable) {
      setDescription(selectedPorts[0]?.description || '');
    }
  }, [isDescriptionEditable, selectedPorts]);

  const selectRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const pluralSuffix = selectedPorts.length !== 1 ? 's' : '';

  const ShowValidationError =
    (!isDescriptionValid && touched.newDescription) || (submitAttempted && !isDescriptionValid);

  const spaceUrl: string = config.gChatSupportSpace;

  const handleSelectChange = (e: SelectChangeEvent) => {
    setSelectedVlan(e.target.value);
  };

  const getFieldHelperText = (): React.ReactNode => {
    if (ShowValidationError) {
      if (description.trim().length === 0) {
        return 'Description cannot be empty.';
      }
      if (!allowedInputCharacters.test(description)) {
        if (isDistro) {
          return (
            <>
              This port&apos;s current description contains characters that aren&apos;t allowed.
              Allowed characters: letters, numbers, hyphens (-), underscores (_), slashes (/),
              colons (:), parentheses, pipes (|), and spaces. If your action is blocked due to this
              validation, please contact{' '}
              <Link href={spaceUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                #ncc-support
              </Link>{' '}
              for assistance.
            </>
          );
        }
        return 'Allowed characters: letters, numbers, hyphens (-), underscores (_), and spaces.';
      }
    }

    if (!isDescriptionEditable) {
      return 'This is the current description of the selected port and cannot be edited.';
    }

    return `${description.length} / 100`;
  };

  return (
    <>
      <DialogContent sx={{ paddingBottom: 0 }}>
        <Box display="flex" flexDirection="column">
          {hasActivePorts && (
            <Box>
              <Typography variant="body1" sx={dialogStyles.warningTypographyStyles}>
                <WarningAmberOutlinedIcon sx={dialogStyles.warningIconStyles} />
                {hasMultipleActivePorts
                  ? 'There are devices currently connected to the selected ports.'
                  : 'There is a device currently connected to the selected port.'}
              </Typography>
            </Box>
          )}
          <FormControl
            required
            error={(!isVlanValid && touched.selectedVlan) || (submitAttempted && !isVlanValid)}
            sx={{ flex: 1 }}
            ref={selectRef}
          >
            <InputLabel id="item-select-label">New VLAN</InputLabel>
            <Select
              labelId="item-select-label"
              value={selectedVlan}
              onChange={handleSelectChange}
              onBlur={markVlanSelectTouched}
              label="New VLAN"
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  justifyContent: 'center',
                  textAlign: 'center',
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 220,
                    overflowY: 'scroll',
                  },
                  sx: {
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#bbb',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f0f0f0',
                    },
                    scrollbarWidth: 'auto',
                  },
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
              renderValue={(selected) => {
                let selectedItem;
                let descriptionText;
                let displayText;

                if (availableVlans?.type === 'juniper') {
                  selectedItem = availableVlans.available_vlans.find(
                    (item) => item.port_usage === selected
                  );
                  if (!selectedItem) return '';
                  descriptionText = ` (${selectedItem.port_usage}, ${selectedItem.mode})`;
                  displayText = `${selectedItem.vlan}${descriptionText}`;
                } else {
                  selectedItem = availableVlans?.available_vlans.find(
                    (item) => item.vlan === selected
                  );
                  if (!selectedItem) return '';
                  const hasDescription = selectedItem.description.trim().length > 0;
                  descriptionText = hasDescription ? ` (${selectedItem.description})` : '';
                  displayText = `${selectedItem.vlan}${descriptionText}`;
                }
                return (
                  <Tooltip title={displayText}>
                    <Box
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {displayText}
                    </Box>
                  </Tooltip>
                );
              }}
            >
              {availableVlans?.type === 'juniper'
                ? availableVlans?.available_vlans.map(({ port_usage, vlan, mode }) => {
                  const hasDescription = port_usage.trim().length > 0;
                  const fullText = hasDescription ? `${vlan} (${port_usage}, ${mode})` : vlan;
                  return (
                    <MenuItem key={port_usage} value={port_usage}>
                      <Box sx={{ whiteSpace: 'normal', lineHeight: 1.4 }}>{fullText}</Box>
                    </MenuItem>
                  );
                })
                : availableVlans?.available_vlans.map(({ vlan, description }) => {
                  const hasDescription = description.trim().length > 0;
                  const fullText = hasDescription ? `${vlan} (${description})` : vlan;
                  return (
                    <MenuItem key={vlan} value={vlan}>
                      <Box sx={{ whiteSpace: 'normal', lineHeight: 1.4 }}>{fullText}</Box>
                    </MenuItem>
                  );
                })}
            </Select>
            <FormHelperText>
              {(!isVlanValid && touched.selectedVlan) || (submitAttempted && !isVlanValid)
                ? 'Please select a VLAN value.'
                : ' '}
            </FormHelperText>
          </FormControl>
          <TextField
            label={isDescriptionEditable ? 'New description' : 'Current Description'}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={markDescriptionTouched}
            required
            error={ShowValidationError}
            helperText={getFieldHelperText()}
            InputProps={{ readOnly: !isDescriptionEditable }}
            inputRef={descriptionRef}
            multiline
            maxRows={3}
            inputProps={{ maxLength: 100 }}
            sx={{
              marginTop: 1,
              '& .MuiInputBase-inputMultiline': {
                fontSize: '1rem',
                lineHeight: 1.5,
                resize: 'none',
              },
              ...(!isDescriptionEditable &&
                !ShowValidationError &&
                dialogStyles.readOnlyTextFieldStyles),
            }}
            placeholder={isDescriptionEditable ? 'Enter description' : undefined}
          />
          <Typography variant="body1" fontWeight="bold">
            Selected port{pluralSuffix} with current VLAN value:
          </Typography>
          <Box
            sx={{
              ...dialogStyles.gridStyles,
              gridTemplateColumns: selectedPorts.length > 1 ? '1fr 1fr' : '1fr',
            }}
          >
            {selectedPorts.map((entity, index) => (
              <Box key={index}>
                <InfoRow label={entity.port} value={`(VLAN: ${entity.vlan})`} />
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
    </>
  );
};

export default PortFlipForm;
